const DEVICE_TELEMETRY_KEY = 'remu_bucket_device_telemetry_v1';
const DEVICE_TELEMETRY_EVENT = 'remu-bucket-device-telemetry-changed';

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const normalizeSerialNumber = (value) => String(value || '').trim();

const readRawDeviceTelemetry = () => {
  if (!canUseStorage()) {
    return [];
  }

  try {
    const storedValue = window.localStorage.getItem(DEVICE_TELEMETRY_KEY);
    if (!storedValue) {
      return [];
    }

    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
};

const writeRawDeviceTelemetry = (devices) => {
  if (!canUseStorage()) {
    return devices;
  }

  window.localStorage.setItem(DEVICE_TELEMETRY_KEY, JSON.stringify(devices));
  window.dispatchEvent(new CustomEvent(DEVICE_TELEMETRY_EVENT, { detail: devices }));

  return devices;
};

export const readDeviceTelemetryStore = () => readRawDeviceTelemetry();

export const upsertDeviceTelemetry = (deviceTelemetry) => {
  const serialNumber = normalizeSerialNumber(deviceTelemetry?.serialNumber);

  if (!serialNumber) {
    return readRawDeviceTelemetry();
  }

  const timestamp = deviceTelemetry?.updatedAt || new Date().toISOString();
  const existingDevices = readRawDeviceTelemetry();
  const nextDevice = {
    serialNumber,
    deviceName: deviceTelemetry?.deviceName || serialNumber,
    customerId: deviceTelemetry?.customerId ?? null,
    organizationCode: deviceTelemetry?.organizationCode || '',
    connected: deviceTelemetry?.connected !== false,
    status: deviceTelemetry?.status || '',
    telemetry: deviceTelemetry?.telemetry || '',
    cameraUrl: deviceTelemetry?.cameraUrl || deviceTelemetry?.camera_url || '',
    cameraSnapshotData: deviceTelemetry?.cameraSnapshotData || '',
    recordingUrl: deviceTelemetry?.recordingUrl || '',
    parsedTelemetry: deviceTelemetry?.parsedTelemetry || { temperature: '', pressure: '' },
    readCount: Number(deviceTelemetry?.readCount || 0),
    updatedAt: timestamp
  };

  const nextDevices = [
    nextDevice,
    ...existingDevices.filter((device) => normalizeSerialNumber(device.serialNumber) !== serialNumber)
  ].sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());

  return writeRawDeviceTelemetry(nextDevices);
};

export const removeDeviceTelemetry = (serialNumber) => {
  const normalizedSerialNumber = normalizeSerialNumber(serialNumber);

  if (!normalizedSerialNumber) {
    return readRawDeviceTelemetry();
  }

  const nextDevices = readRawDeviceTelemetry().filter(
    (device) => normalizeSerialNumber(device.serialNumber) !== normalizedSerialNumber
  );

  return writeRawDeviceTelemetry(nextDevices);
};

export const subscribeDeviceTelemetry = (onChange) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleStorageChange = (event) => {
    if (event.key === DEVICE_TELEMETRY_KEY) {
      onChange(readRawDeviceTelemetry());
    }
  };

  const handleCustomChange = (event) => {
    onChange(Array.isArray(event.detail) ? event.detail : readRawDeviceTelemetry());
  };

  window.addEventListener('storage', handleStorageChange);
  window.addEventListener(DEVICE_TELEMETRY_EVENT, handleCustomChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener(DEVICE_TELEMETRY_EVENT, handleCustomChange);
  };
};